import React, { useState } from 'react';
import {
    LayoutDashboard,
    Users,
    Mail,
    Settings,
    ShieldCheck,
    Activity,
    Menu,
    X,
    LogOut,
    ChevronRight,
    Search,
    UserPlus
} from 'lucide-react';
import AdminDashboard from '../../components/admin/AdminDashboard.jsx';
import UserManagement from '../../components/admin/UserManagement.jsx';
import EmailMonitoring from '../../components/admin/EmailMonitoring.jsx';
import ServerSettings from '../../components/admin/ServerSettings.jsx';
import AuditLogs from '../../components/admin/AuditLogs.jsx';
import AccountProvisioning from '../../components/admin/AccountProvisioning.jsx';
import DomainManagement from '../../components/admin/DomainManagement.jsx';
import PolicyManagement from '../../components/admin/PolicyManagement.jsx';
import { Globe, ShieldAlert } from 'lucide-react';
import SharedMailboxManagement from '../../components/admin/SharedMailboxManagement.jsx';

import MFAControlCenter from '../../components/admin/MFAControlCenter.jsx';

const AdminPage = ({ onLogout }) => {
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);


    const menuItems = [
        { id: 'dashboard', label: 'System Health', icon: <LayoutDashboard size={20} /> },
        { id: 'provision', label: 'Provision Identity', icon: <UserPlus size={20} /> },
        { id: 'domains', label: 'Domain Management', icon: <Globe size={20} /> },
        { id: 'users', label: 'Mailbox Registry', icon: <Users size={20} /> },
        { id: 'mfa', label: '2MFA Governance', icon: <ShieldCheck size={20} /> },
        { id: 'monitoring', label: 'Mail Flow Monitor', icon: <Mail size={20} /> },
        { id: 'settings', label: 'MTA Configuration', icon: <Settings size={20} /> },
        { id: 'policies', label: 'Global Policies', icon: <ShieldAlert size={20} /> },
        { id: 'shared', label: 'Shared Mailboxes', icon: <Users size={20} /> },
        { id: 'logs', label: 'Audit Trail', icon: <Activity size={20} /> },
    ];

    const renderContent = () => {
        const components = {
            dashboard: <AdminDashboard />,
            provision: <AccountProvisioning />,
            domains: <DomainManagement />,
            users: <UserManagement />,
            mfa: <MFAControlCenter />,
            monitoring: <EmailMonitoring />,
            settings: <ServerSettings />,
            policies: <PolicyManagement />,
            shared: <SharedMailboxManagement />,
            logs: <AuditLogs />
        };
        return <div className="animate-slide-in">{components[activeTab] || <AdminDashboard />}</div>;
    };


    return (
        <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-300">
            {/* Elegant Sidebar - Redesigned for Premium Look */}
            <aside className={`${isSidebarOpen ? 'w-64' : 'w-20'} bg-white dark:bg-slate-900 transition-all duration-500 cubic-bezier(0.4, 0, 0.2, 1) flex flex-col z-30 shadow-[4px_0_32px_-12px_rgba(0,0,0,0.08)]`}>
                <div className="p-6 flex items-center space-x-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden bg-white shadow-sm ring-1 ring-slate-100 dark:ring-slate-800">
                        <img src="/logo.svg" alt="XeMail Logo" className="w-8 h-8 object-contain" />
                    </div>
                    {isSidebarOpen && (
                        <div className="flex flex-col">
                            <span className="font-bold text-lg tracking-tight text-slate-900 dark:text-white leading-tight">XeMail MTA</span>
                            <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#4169E1]">Enterprise Admin</span>
                        </div>
                    )}
                </div>

                <div className="px-3 py-6 flex-1 space-y-1 overflow-y-auto custom-scrollbar">
                    {isSidebarOpen && <p className="px-4 text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 opacity-70">Infrastructure</p>}
                    {menuItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`w-full flex items-center group relative ${isSidebarOpen ? 'px-4 py-2.5' : 'justify-center py-3'} rounded-xl transition-all duration-300 ${activeTab === item.id
                                ? 'bg-[#4169E1]/10 text-[#4169E1] font-bold'
                                : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                }`}
                        >
                            {activeTab === item.id && (
                                <div className="absolute left-0 w-1 h-6 bg-[#4169E1] rounded-r-full"></div>
                            )}
                            <span className={`transition-all duration-300 ${activeTab === item.id ? 'text-[#4169E1]' : 'group-hover:text-slate-700 dark:group-hover:text-slate-300'}`}>
                                {item.icon}
                            </span>
                            {isSidebarOpen && (
                                <span className="ml-3.5 tracking-wide flex-1 text-left text-sm">{item.label}</span>
                            )}
                            {isSidebarOpen && activeTab === item.id && (
                                <ChevronRight size={14} className="opacity-40" />
                            )}
                        </button>
                    ))}
                </div>

                <div className="p-3 mt-auto">
                    <button
                        onClick={onLogout}
                        className={`w-full flex items-center ${isSidebarOpen ? 'px-4' : 'justify-center'} py-3 text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-all duration-300 text-sm font-semibold group`}
                    >
                        <LogOut size={18} className="group-hover:-translate-x-1 transition-transform" />
                        {isSidebarOpen && <span className="ml-3">Sign Out</span>}
                    </button>
                </div>
            </aside>

            {/* Seamless Main Content Wrapper */}
            <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50/50 dark:bg-slate-950 relative">
                {/* Modern Floating Header - Refined and Integrated */}
                <header className="h-16 shrink-0 flex items-center justify-between px-8 z-20 bg-white/40 dark:bg-slate-900/40 backdrop-blur-2xl border-b border-slate-200/30 dark:border-slate-800/30">
                    <div className="flex items-center space-x-6">
                        <button
                            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                            className="p-2.5 hover:bg-white dark:hover:bg-slate-800 rounded-xl text-slate-500 transition-all shadow-sm border border-slate-200/50 dark:border-slate-700/50 active:scale-95"
                        >
                            {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>

                    <div className="flex items-center space-x-2">


                        <div className="h-4 w-[1px] bg-slate-200 dark:bg-slate-800 mx-2"></div>

                        <div className="flex items-center space-x-3 pl-1">
                            <div className="flex flex-col items-end hidden sm:flex">
                                <span className="text-sm font-bold text-slate-900 dark:text-white">Admin Manager</span>
                                <span className="text-xs text-[#4169E1] font-bold flex items-center uppercase tracking-wider">
                                    <span className="w-1.5 h-1.5 bg-[#4169E1] rounded-full mr-1 animate-pulse"></span>
                                    System Online
                                </span>
                            </div>
                            <div className="w-9 h-9 rounded-lg bg-[#4169E1]/10 dark:bg-[#4169E1]/30 flex items-center justify-center text-[#4169E1] font-bold border border-[#4169E1]/20 dark:border-[#4169E1]/50 shadow-sm overflow-hidden">
                                <img src="https://ui-avatars.com/api/?name=Admin+Manager&background=4169E1&color=fff&bold=true" alt="Profile" />
                            </div>
                        </div>
                    </div>
                </header>

                {/* Content Area with refined padding */}
                <main className="flex-1 overflow-y-auto px-6 sm:px-12 py-10 custom-scrollbar">
                    {renderContent()}
                </main>
            </div>
        </div>
    );
};

export default AdminPage;
